import React, { lazy } from "react";
import { ProtectedRoute } from "./core";
import { useUser } from "./featured/User";

// The app's route table.
//
// Routing is an application concern, not a feature block's - a block exports
// pages and access rules, and this file decides which URL renders what. As each
// remaining section is extracted, its routes move out of App.js and are appended
// here.

// Imported from the page files directly rather than through
// featured/User/pages/index.js: a static barrel import would pull both bundles
// into the entry chunk and undo the code splitting.
//
// App.js already wraps <Routes> in a <Suspense fallback={<Preloader />}>, so
// these need no boundary of their own.
const AuthPage = lazy(() => import("./featured/User/pages/Auth/Auth"));
const ProfilePage = lazy(() => import("./featured/User/pages/Profile/Profile"));

/**
 * Signed-in-only gate, built from the User block's session state and the generic
 * guard in core/middleware. Assembling it here keeps `core` free of any feature
 * dependency.
 *
 * `authLoading` matters as much as `isAuthenticated`: the session is seeded
 * optimistically from localStorage, so acting on it before the server has
 * confirmed it flashes a redirect to /auth on every reload for users who are in
 * fact signed in.
 */
const RequireAuth = ({ children }) => {
  const { isAuthenticated, authLoading } = useUser();

  return (
    <ProtectedRoute
      isAllowed={isAuthenticated}
      isLoading={authLoading}
      redirectTo="/auth"
    >
      {children}
    </ProtectedRoute>
  );
};

/**
 * Routes for the extracted blocks, spread into <Routes> by App.js.
 *
 * Not here: /profile/:name. Despite the path it renders a photographer bio page
 * belonging to the gallery section, so App.js still declares it inline until
 * that section is extracted.
 */
export const appRoutes = [
  { path: "/auth", element: <AuthPage /> },
  {
    path: "/profile",
    element: (
      <RequireAuth>
        <ProfilePage />
      </RequireAuth>
    ),
  },
];

export default appRoutes;
